import gql from "graphql-tag";

import {gqlOp, type GqlOperation} from "@repo/data-gql-core";

import type {
    ClassroomByCode,
    ClassroomByCodeVariables,
} from "./__graphql-types__/ClassroomByCode.ts";
import type {
    clearRecommendationFor321ContentItem,
    clearRecommendationFor321ContentItemVariables,
} from "./__graphql-types__/clearRecommendationFor321ContentItem.ts";
import type {
    ContentForLearnableContent as ContentForLearnableContentResult,
    ContentForLearnableContentVariables,
} from "./__graphql-types__/ContentForLearnableContent.ts";
import type {
    ContentForPath as ContentForPathResult,
    ContentForPathVariables,
} from "./__graphql-types__/ContentForPath.ts";
import type {
    courseProgressQuery,
    courseProgressQueryVariables,
} from "./__graphql-types__/courseProgressQuery.ts";
import type {
    isEligibleForMasteryChallenge as IsEligibleForMasteryChallenge,
    isEligibleForMasteryChallengeVariables as IsEligibleForMasteryChallengeVariables,
} from "./__graphql-types__/isEligibleForMasteryChallenge.ts";
import type {
    MappedStandardsForContent as MappedStandardsForContentResult,
    MappedStandardsForContentVariables,
} from "./__graphql-types__/MappedStandardsForContent.ts";
import type {
    QAExpandKeyInfo,
    QAExpandKeyInfoVariables,
} from "./__graphql-types__/QAExpandKeyInfo.ts";
import type {
    resetChallengeProgress,
    resetChallengeProgressVariables,
} from "./__graphql-types__/resetChallengeProgress.ts";
import type {
    resetProjectProgress,
    resetProjectProgressVariables,
} from "./__graphql-types__/resetProjectProgress.ts";
import type {
    RestartTask,
    RestartTaskVariables,
} from "./__graphql-types__/RestartTask.ts";
import type {
    UntranslatedContentCheck as UntranslatedContentCheckResult,
    UntranslatedContentCheckVariables,
} from "./__graphql-types__/UntranslatedContentCheck.ts";
import type {
    updateChallengeProgress,
    updateChallengeProgressVariables,
} from "./__graphql-types__/updateChallengeProgress.ts";
import type {
    updateInteractiveProgress,
    updateInteractiveProgressVariables,
} from "./__graphql-types__/updateInteractiveProgress.ts";
import type {
    updateProjectProgress,
    updateProjectProgressVariables,
} from "./__graphql-types__/updateProjectProgress.ts";
import type {
    updateTalkthroughProgress,
    updateTalkthroughProgressVariables,
} from "./__graphql-types__/updateTalkthroughProgress.ts";
import type {
    UserAssignmentsForContent,
    UserAssignmentsForContentVariables,
} from "./__graphql-types__/UserAssignmentsForContent.ts";
import type {
    UserAssignmentsPage,
    UserAssignmentsPageVariables,
} from "./__graphql-types__/UserAssignmentsPage.ts";
import type {UserNextUpcomingAssignment} from "./__graphql-types__/UserNextUpcomingAssignment.ts";
import type {
    UserPermissionsForContent,
    UserPermissionsForContentVariables,
} from "./__graphql-types__/UserPermissionsForContent.ts";
import type {
    userProgressForAssignmentsVariables as UserAssignmentProgressVariables,
    userProgressForAssignments as UserAssignmentsProgress,
} from "./__graphql-types__/userProgressForAssignments.ts";
import type {
    userProgressForLesson as UserLessonProgress,
    userProgressForLessonVariables as UserLessonProgressVariables,
} from "./__graphql-types__/userProgressForLesson.ts";
import type {
    userProgressForPrograms,
    userProgressForProgramsVariables,
} from "./__graphql-types__/userProgressForPrograms.ts";

const LEARNABLE_CONTENT_FRAGMENT = gql`
    fragment LearnableContentData on LearnableContent {
        id
        contentKind
        slug
        translatedTitle

        ... on Article {
            articleClarificationsEnabled: clarificationsEnabled
            translatedDescription
            translatedPerseusContent
        }

        ... on Challenge {
            authorList {
                name
            }
            canvasOnly
            code
            codeFormat
            configVersion
            defaultUrlPath
            height
            nodeSlug
            translatedDescription
            translatedTests
            testsFormat
            testStrings {
                message
            }
            userAuthoredContentType
            width
        }

        ... on Exercise {
            problemTypeKind
        }

        ... on Interactive {
            authorList {
                name
            }
            canvasOnly
            code
            codeFormat
            configVersion
            defaultUrlPath
            height
            nodeSlug
            translatedDescription
            userAuthoredContentType
            width
        }

        ... on Project {
            authorList {
                name
            }
            canvasOnly
            code
            codeFormat
            configVersion
            defaultUrlPath
            height
            nodeSlug
            translatedDescription
            translatedProjectEval
            translatedProjectEvalTips
            userAuthoredContentType
            width
        }

        ... on Talkthrough {
            authorList {
                name
            }
            canvasOnly
            code
            configVersion
            defaultUrlPath
            height
            nodeSlug
            playback
            subtitles {
                endTime
                kaIsValid
                startTime
                text
            }
            translatedDescription
            translatedMp3Url
            userAuthoredContentType
            width
            youtubeId
        }

        ... on TopicQuiz {
            index
            exerciseLength
            timeEstimate {
                lowerBound
                upperBound
            }
            coveredTutorials {
                id
                translatedTitle
                relativeUrl
                allLearnableContent {
                    id
                    contentKind
                }
            }
        }

        ... on TopicUnitTest {
            exerciseLength
            timeEstimate {
                lowerBound
                upperBound
            }
            coveredTutorials {
                id
                translatedTitle
                relativeUrl
                allLearnableContent {
                    id
                    contentKind
                }
            }
        }

        ... on Video {
            authorNames
            # Aliased because the schema of authorList is different for the
            # different content types. We won't need to alias this if in the
            # future, the GraphQL schemas authorList matched for all content
            # types.
            videoAuthorList: authorList {
                name
            }
            clarificationsEnabled
            dateAdded
            description
            downloadUrls
            duration
            imageUrl
            kaUrl
            kaUserLicense
            keywords
            readableId
            sha
            thumbnailUrls {
                category
                url
            }
            translatedDescriptionHtml
            translatedYoutubeId
            translatedYoutubeLang
            youtubeId
            augmentedTranscript
            relativeUrl
            descriptionHtml
            nodeSlug
            translatedDescription
            translatedCustomTitleTag
            subtitles {
                endTime
                kaIsValid
                startTime
                text
            }
            keyMoments {
                startOffset
                endOffset
                label
            }
            educationalLevel
            learningResourceType
        }
    }
`;

const LEARNABLE_CONTENT_METADATA_FRAGMENT = gql`
    fragment LearnableContentMetadata on LearnableContent {
        id
        canonicalUrl: defaultUrlPath
        contentDescriptor
        contentKind
        parentTopic {
            id
            parent {
                id
                masteryEnabled
            }
        }
        progressKey
        slug
        translatedCustomTitleTag
        translatedDescription
        translatedTitle
        urlWithinCurationNode
        ... on Challenge {
            userAuthoredContentType
        }
        ... on Interactive {
            userAuthoredContentType
        }
        ... on Project {
            userAuthoredContentType
        }
    }
`;

const QUIZ_METADATA_FRAGMENT = gql`
    fragment QuizMetadata on TopicQuiz {
        ...LearnableContentMetadata
        exerciseLength
        index
        timeEstimate {
            lowerBound
            upperBound
        }
    }

    ${LEARNABLE_CONTENT_METADATA_FRAGMENT}
`;

const UNIT_TEST_METADATA_FRAGMENT = gql`
    fragment UnitTestMetadata on TopicUnitTest {
        ...LearnableContentMetadata
        exerciseLength
        timeEstimate {
            lowerBound
            upperBound
        }
    }

    ${LEARNABLE_CONTENT_METADATA_FRAGMENT}
`;

const LESSON_FRAGMENT = gql`
    fragment LessonData on Lesson {
        id
        relativeUrl
        slug
        translatedDescription
        translatedTitle
        key
        # NOTE: Need to vet this further. In the Content Library we used
        # other fields to obtain the children (e.g. childrenWithUnlisted)
        # Those resolvers, had filters applied to take into account listedness,
        # include/exclude lists.
        curatedChildren(includeUnlisted: false) {
            ... on LearnableContent {
                ...LearnableContentMetadata
            }
            ... on Exercise {
                exerciseLength
                isSkillCheck
                sponsored
                thumbnailUrl
                timeEstimate {
                    lowerBound
                    upperBound
                }
            }
        }
    }

    ${LEARNABLE_CONTENT_METADATA_FRAGMENT}
`;

const UNIT_FRAGMENT = gql`
    fragment UnitData on Unit {
        id
        iconPath
        masteryEnabled
        relativeUrl
        slug
        isListedForLearners
        translatedCustomTitleTag
        translatedDescription
        translatedTitle
        unlistedAncestorIds
    }
`;

const COURSE_FRAGMENT = gql`
    fragment CourseData on Course {
        id
        iconPath
        masteryEnabled
        relativeUrl
        slug
        translatedTitle
        translatedDescription
        isListedForLearners
        translatedCustomTitleTag
        contentKind
        userAuthoredContentTypes
        masterableExercises(includeDuplicates: true) {
            id
        }

        # Needed for breadcrumbs. We expect that this will always be a Domain.
        parent {
            id
            contentKind
            relativeUrl
            slug
            translatedTitle
        }

        # Module information for rendering course page.
        lowerToc
        curation {
            hideSubjectIntro
            hideCommunityQuestions
            sponsorFooterAttribution {
                footnoteHtml
                imageBaselineAligned
                imageCaption
                imageUrl
                taglineHtml
            }
            modules {
                kind
                untranslatedFields

                # Mappers doesn't have any additional fields.
                # BrowseProjects doesn't have any additional fields.

                ... on CourseIntroModule {
                    callToAction
                    description
                    link
                    title
                    video
                }

                ... on ActionListModule {
                    actions {
                        text
                        URL: url
                        contentDescriptor
                    }
                    kind
                    title
                }

                ... on PartnershipDescriptionModule {
                    description
                    imageCaption
                    imageUrl
                    isOutro
                    partnerUrl
                    partnerUrlText
                }

                ... on ContentCarouselModule {
                    referrer
                    title
                    contentDescriptors
                }
            }
            excludedChildren
        }
        courseChallenge {
            id
            contentKind
            slug
            contentDescriptor
            parentTopic {
                id
                parent {
                    id
                    masteryEnabled
                }
            }
            urlWithinCurationNode
            exerciseLength
            timeEstimate {
                lowerBound
                upperBound
            }
        }

        # we can't use a fragment to get the same fields as CourseChallenge
        # because they do not share an interface that includes exerciseLength
        masteryChallenge {
            id
            contentKind
            slug
            contentDescriptor
            parentTopic {
                id
                parent {
                    id
                    masteryEnabled
                }
            }
            urlWithinCurationNode
            exerciseLength
            timeEstimate {
                lowerBound
                upperBound
            }
        }
    }
`;

const BASIC_CONTENT_ITEM_PROGRESS_FRAGMENT = gql`
    fragment BasicContentItemProgress on ContentItemProgress {
        bestScore {
            numAttempted
            numCorrect
            completedDate
        }
        completionStatus
        content {
            id
            contentKind
            contentDescriptor
            progressKey
        }
    }
`;

// The MappedStandardsForContent query is a client side query to get the
// mapped standards of a content item on our content pages.
export const REGIONAL_STANDARDS_QUERY: GqlOperation<
    MappedStandardsForContentResult,
    MappedStandardsForContentVariables
> = gqlOp(gql`
    query MappedStandardsForContent(
        $contentDescriptor: String!
        $locale: String
        $region: String
    ) {
        standards: standardMappingsForContent(
            contentDescriptor: $contentDescriptor
            locale: $locale
            region: $region
        ) {
            setId
            id
            standardId
            description
            relativeUrl
            setOfStandards {
                id
                isDiscoverable
            }
        }
    }
`);

/**
 * The ContentForPath query is used to populate Web's version of the Streaming
 * Topic Tree (STT) with Course data. Given a path, we fetch the Course and
 * all of its descendants.
 */

export const CONTENT_ROUTE_QUERY: GqlOperation<
    ContentForPathResult,
    ContentForPathVariables
> = gqlOp(gql`
    query ContentForPath($path: String!, $countryCode: String!) {
        content {
            metadata {
                commitSha
            }
        }
        contentRoute(path: $path, countryCode: $countryCode) {
            resolvedPath

            # Only populated if "resolvedPath" is listed.
            listedPathData {
                course {
                    ...CourseData
                    unitChildren {
                        ...UnitData
                        allOrderedChildren {
                            ... on Lesson {
                                ...LessonData
                            }
                            ... on TopicQuiz {
                                ...QuizMetadata
                            }
                            ... on TopicUnitTest {
                                ...UnitTestMetadata
                            }
                        }
                    }
                }

                lesson {
                    ...LessonData
                }

                content {
                    ...LearnableContentData
                }
            }

            # Only populated if "resolvedPath" is unlisted. Since we support
            # ability to load unlisted content, we need to request data catered
            # to rendering these scenarios. When we are on an unlisted page,
            # we usually render their unlisted children.
            unlistedPathData {
                course {
                    ...CourseData
                    unitChildren {
                        ...UnitData
                        allOrderedChildren {
                            ... on Lesson {
                                ...LessonData
                            }
                            ... on TopicQuiz {
                                ...QuizMetadata
                            }
                            ... on TopicUnitTest {
                                ...UnitTestMetadata
                            }
                        }
                    }
                }

                lesson {
                    ...LessonData
                }

                content {
                    ...LearnableContentData
                }
            }
        }
    }

    ${COURSE_FRAGMENT}
    ${UNIT_FRAGMENT}
    ${LESSON_FRAGMENT}
    ${LEARNABLE_CONTENT_FRAGMENT}
    ${QUIZ_METADATA_FRAGMENT}
    ${UNIT_TEST_METADATA_FRAGMENT}
`);

// Since we are only interested in whether the content exists,
// we do not need to worry about the published content version.
export const UNTRANSLATED_CONTENT_QUERY: GqlOperation<
    UntranslatedContentCheckResult,
    UntranslatedContentCheckVariables
> = gqlOp(gql`
    query UntranslatedContentCheck($path: String!, $countryCode: String!) {
        contentRoute(path: $path, countryCode: $countryCode) {
            resolvedPath
        }
    }
`);

/**
 * Fetch a specific piece of learnable content.
 */
export const LEARNABLE_CONTENT_QUERY: GqlOperation<
    ContentForLearnableContentResult,
    ContentForLearnableContentVariables
> = gqlOp(gql`
    query ContentForLearnableContent($id: String!, $kind: String!) {
        content {
            metadata {
                commitSha
            }
        }
        learnableContent(id: $id, kind: $kind) {
            ...LearnableContentData
        }
    }

    ${LEARNABLE_CONTENT_FRAGMENT}
`);

const ASSIGNMENT_FRAGMENT = gql`
    fragment AssignmentData on Assignment {
        id
        title
        contents {
            id
            contentDescriptor
            contentKind
            slug
            translatedTitle
            parentTopic {
                id
                parent {
                    id
                    masteryEnabled
                    translatedTitle
                }
            }
            topicPaths {
                path {
                    id
                    slug
                    kind
                }
            }
            urlWithinCurationNode
            ... on TopicQuiz {
                index
            }
        }
        courseId
        domainId
        dueDate
        exerciseConfig {
            itemPickerStrategy
        }
        itemCompletionStates {
            state
            exerciseAttempts {
                id
                isCompleted
                isRestarted
                lastAttemptDate
                numCorrect
                numAttempted
            }
        }
        key
        lesson {
            id
            parent {
                id
                masteryEnabled
                translatedTitle
            }
        }
        lessonId
        unit {
            id
            masteryEnabled
            translatedTitle
        }
        unitId
    }
`;

const CLASSROOM_FRAGMENT = gql`
    fragment Classroom on StudentList {
        cacheId
        coachKaid
        descriptor
        hasAssignments
        id
        name
        signupCode
    }
`;

/**
 * Given a classCode, fetch information about the classroom.
 *
 * TODO (LP-11877) Replace this with a query that uses classroom UUID when it
 * exists.
 */
export const CLASSROOM_BY_CODE_QUERY: GqlOperation<
    ClassroomByCode,
    ClassroomByCodeVariables
> = gqlOp(gql`
    query ClassroomByCode($classCode: String!) {
        studentList(classCode: $classCode) {
            cacheId
            coachKaid
            descriptor
            hasAssignments
            id
            name
            signupCode
        }
    }
`);

/**
 * A query used to fetch additional pages of assignments for a given user and
 * classroom.
 */
export const USER_ASSIGNMENTS_PAGE_QUERY: GqlOperation<
    UserAssignmentsPage,
    UserAssignmentsPageVariables
> = gqlOp(gql`
    query UserAssignmentsPage(
        $classroomDescriptor: String!
        $dueAfterISO: DateTime
        $pageSize: Int!
        $cursor: ID
    ) {
        user {
            id
            assignmentsPage(
                dueAfter: $dueAfterISO
                orderBy: DUE_DATE_ASC
                pageSize: $pageSize
                classroomDescriptor: $classroomDescriptor
                after: $cursor
            ) {
                assignments {
                    ...AssignmentData
                }
                pageInfo {
                    nextCursor
                }
            }
        }
    }

    ${ASSIGNMENT_FRAGMENT}
`);

/**
 * Fetches all assignments for the user's default classroom.
 */
export const USER_NEXT_UPCOMING_ASSIGNMENT_QUERY: GqlOperation<UserNextUpcomingAssignment> =
    gqlOp(gql`
        query UserNextUpcomingAssignment($contentDescriptors: [String!]!) {
            user {
                id
                assignmentDueNext {
                    id
                    studentList {
                        ...Classroom
                    }
                }
                assignmentsPageForContentItems(
                    contentDescriptors: $contentDescriptors
                ) {
                    assignments {
                        id
                        studentList {
                            ...Classroom
                        }
                    }
                }
            }
        }

        ${CLASSROOM_FRAGMENT}
    `);

/**
 * Fetches assignments within a specific classroom for a given list of content
 * descriptors.
 */
export const USER_ASSIGNMENTS_FOR_CONTENT: GqlOperation<
    UserAssignmentsForContent,
    UserAssignmentsForContentVariables
> = gqlOp(gql`
    query UserAssignmentsForContent(
        $classroomDescriptor: String!
        $contentDescriptors: [String!]!
    ) {
        user {
            id
            assignmentDueNextForContentDescriptors(
                classroomDescriptor: $classroomDescriptor
                contentDescriptors: $contentDescriptors
            ) {
                ...AssignmentData
            }
        }
    }

    ${ASSIGNMENT_FRAGMENT}
`);

/**
 * Fetches all progress data for a user, for a given lesson.
 */
export const USER_PROGRESS_FOR_LESSON_QUERY: GqlOperation<
    UserLessonProgress,
    UserLessonProgressVariables
> = gqlOp(gql`
    query userProgressForLesson(
        $lessonId: String!
        $unitId: String!
        $masteryEnabled: Boolean!
    ) {
        user {
            id
            contentItemProgresses(queryBy: {parentTopicId: $lessonId}) {
                ...BasicContentItemProgress
                ... on ExerciseItemProgress @include(if: $masteryEnabled) {
                    lastCompletedAttempt {
                        id
                        lastAttemptDate
                        numCorrect
                        numAttempted
                    }
                    updatedMasteryLevel
                }
            }
            latestQuizAttempts(topicId: $unitId) {
                id
                numCorrect
                numAttempted
                isCompleted
                positionKey
            }
            latestUnitTestAttempts(unitId: $unitId) {
                id
                numCorrect
                numAttempted
                isCompleted
                topicId
            }
        }
    }

    ${BASIC_CONTENT_ITEM_PROGRESS_FRAGMENT}
`);

/**
 * Fetches all progress data for a user, for a given set of content descriptors.
 */
export const USER_PROGRESS_FOR_ASSIGNMENTS_QUERY: GqlOperation<
    UserAssignmentsProgress,
    UserAssignmentProgressVariables
> = gqlOp(gql`
    query userProgressForAssignments($contentDescriptors: [String!]!) {
        user {
            id
            contentItemProgresses(
                queryBy: {contentDescriptors: $contentDescriptors}
            ) {
                ...BasicContentItemProgress
                ... on ExerciseItemProgress {
                    lastCompletedAttempt {
                        id
                        lastAttemptDate
                        numCorrect
                        numAttempted
                    }
                    updatedMasteryLevel
                }
            }
        }
    }

    ${BASIC_CONTENT_ITEM_PROGRESS_FRAGMENT}
`);

export const IS_ELIGIBLE_FOR_MASTERY_CHALLENGE_QUERY: GqlOperation<
    IsEligibleForMasteryChallenge,
    IsEligibleForMasteryChallengeVariables
> = gqlOp(gql`
    query isEligibleForMasteryChallenge($courseId: String!) {
        user {
            id
            exerciseData {
                masteryChallengeStatus(courseId: $courseId) {
                    isEligible
                }
            }
        }
    }
`);

export const CLEAR_RECOMMENDATION_FOR_321_CONTENT_ITEM: GqlOperation<
    clearRecommendationFor321ContentItem,
    clearRecommendationFor321ContentItemVariables
> = gqlOp(gql`
    mutation clearRecommendationFor321ContentItem(
        $contentDescriptor: String!
        $unitId: String!
    ) {
        clearRecommendation(
            contentDescriptor: $contentDescriptor
            unitId: $unitId
        ) {
            wasSuccessful
        }
    }
`);

export const USER_CONTENT_PERMISSIONS_QUERY: GqlOperation<
    UserPermissionsForContent,
    UserPermissionsForContentVariables
> = gqlOp(gql`
    query UserPermissionsForContent {
        user {
            id
            canEdit: hasPermission(
                name: "can_edit_content"
                scope: ANY_ON_CURRENT_LOCALE
            )
        }
    }
`);

export const QA_EXPAND_KEY_INFO_QUERY: GqlOperation<
    QAExpandKeyInfo,
    QAExpandKeyInfoVariables
> = gqlOp(gql`
    query QAExpandKeyInfo($encryptedKey: String) {
        qaExpandKeyInfo(encryptedQaExpandKey: $encryptedKey) {
            feedbackType
            unencryptedKey
        }
    }
`);

export const RESTART_TASK_MUTATION: GqlOperation<
    RestartTask,
    RestartTaskVariables
> = gqlOp(gql`
    mutation RestartTask($key: String!) {
        restartTask(input: {key: $key}) {
            error {
                code
                debugMessage
            }
            task {
                id
                ... on PracticeTask {
                    exerciseLength
                    taskAttemptHistory {
                        itemId
                    }
                }
                ... on TopicQuizTask {
                    taskAttemptHistory {
                        itemId
                    }
                }
                ... on TopicUnitTestTask {
                    taskAttemptHistory {
                        itemId
                    }
                }
            }
        }
    }
`);

export const PROGRAM_ITEMS_PROGRESS_QUERY: GqlOperation<
    userProgressForPrograms,
    userProgressForProgramsVariables
> = gqlOp(gql`
    query userProgressForPrograms($contentDescriptors: [String!]!) {
        user {
            id
            contentItemProgresses(
                queryBy: {contentDescriptors: $contentDescriptors}
            ) {
                completionStatus
                content {
                    id
                    contentKind
                }
                ... on ChallengeItemProgress {
                    currentStep
                    points
                    stashedCode
                }
                ... on InteractiveItemProgress {
                    points
                }
                ... on ProjectItemProgress {
                    points
                    stashedCode
                }
                ... on TalkthroughItemProgress {
                    points
                    lastMsWatched
                }
            }
        }
    }
`);

export type UpdateChallengeProgressData = updateChallengeProgress;
export type UpdateChallengeProgressVariables = updateChallengeProgressVariables;
/**
 * Updates the user's progress for Challenge content types.
 */
export const UPDATE_CHALLENGE_PROGRESS_MUTATION: GqlOperation<
    UpdateChallengeProgressData,
    UpdateChallengeProgressVariables
> = gqlOp(gql`
    mutation updateChallengeProgress(
        $currentStep: Int
        $programId: ID!
        $lessonId: ID!
        $stashedCode: String
        $status: Int
    ) {
        updateChallengeProgress(
            input: {
                currentStep: $currentStep
                programId: $programId
                lessonId: $lessonId
                stashedCode: $stashedCode
                status: $status
            }
        ) {
            actionResults {
                pointsEarned {
                    points
                }
                notificationsAdded {
                    badges
                    avatarParts
                    readable
                    urgent
                    toast
                    continueUrl
                }
                tutorialNodeProgress {
                    contentId
                    progress
                }
                userProfile {
                    countVideosCompleted
                    points
                    countBrandNewNotifications
                }
            }
            programProgress {
                currentStep
                completionStatus
                content {
                    id
                    contentKind
                }
                points
                stashedCode
            }
            error {
                code
            }
        }
    }
`);

export type ResetChallengeProgressData = resetChallengeProgress;
export type ResetChallengeProgressVariables = resetChallengeProgressVariables;
/**
 * Resets the user's progress for Challenge content types.
 */
export const RESET_CHALLENGE_PROGRESS_MUTATION: GqlOperation<
    ResetChallengeProgressData,
    ResetChallengeProgressVariables
> = gqlOp(gql`
    mutation resetChallengeProgress($programId: ID!, $lessonId: ID!) {
        resetChallengeProgress(
            input: {programId: $programId, lessonId: $lessonId}
        ) {
            programProgress {
                completionStatus
                content {
                    id
                    contentKind
                }
                points
                stashedCode
            }
            error {
                code
            }
        }
    }
`);

export type UpdateInteractiveProgressData = updateInteractiveProgress;
export type UpdateInteractiveProgressVariables =
    updateInteractiveProgressVariables;
/**
 * Updates the user's progress for Interactive content types.
 */
export const UPDATE_INTERACTIVE_PROGRESS_MUTATION: GqlOperation<
    UpdateInteractiveProgressData,
    UpdateInteractiveProgressVariables
> = gqlOp(gql`
    mutation updateInteractiveProgress($programId: ID!, $lessonId: ID!) {
        updateInteractiveProgress(
            input: {programId: $programId, lessonId: $lessonId}
        ) {
            actionResults {
                pointsEarned {
                    points
                }
                notificationsAdded {
                    badges
                    avatarParts
                    readable
                    urgent
                    toast
                    continueUrl
                }
                tutorialNodeProgress {
                    contentId
                    progress
                }
                userProfile {
                    countVideosCompleted
                    points
                    countBrandNewNotifications
                }
            }
            programProgress {
                completionStatus
            }
            error {
                code
            }
        }
    }
`);

export type UpdateProjectProgressData = updateProjectProgress;
export type UpdateProjectProgressVariables = updateProjectProgressVariables;
/**
 * Updates the user's progress for Interactive content types.
 */
export const UPDATE_PROJECT_PROGRESS_MUTATION: GqlOperation<
    UpdateProjectProgressData,
    UpdateProjectProgressVariables
> = gqlOp(gql`
    mutation updateProjectProgress(
        $programId: ID!
        $lessonId: ID!
        $stashedCode: String
        $status: Int
    ) {
        updateProjectProgress(
            input: {
                programId: $programId
                lessonId: $lessonId
                stashedCode: $stashedCode
                status: $status
            }
        ) {
            actionResults {
                pointsEarned {
                    points
                }
                notificationsAdded {
                    badges
                    avatarParts
                    readable
                    urgent
                    toast
                    continueUrl
                }
                tutorialNodeProgress {
                    contentId
                    progress
                }
                userProfile {
                    countVideosCompleted
                    points
                    countBrandNewNotifications
                }
            }
            programProgress {
                completionStatus
                content {
                    id
                    contentKind
                }
                points
                stashedCode
            }
            error {
                code
            }
        }
    }
`);

export type ResetProjectProgressData = resetProjectProgress;
export type ResetProjectProgressVariables = resetProjectProgressVariables;

export const RESET_PROJECT_PROGRESS_MUTATION: GqlOperation<
    ResetProjectProgressData,
    ResetProjectProgressVariables
> = gqlOp(gql`
    mutation resetProjectProgress($programId: ID!, $lessonId: ID!) {
        resetProjectProgress(
            input: {programId: $programId, lessonId: $lessonId}
        ) {
            programProgress {
                completionStatus
                content {
                    id
                    contentKind
                }
                points
                stashedCode
            }
            error {
                code
            }
        }
    }
`);

export type UpdateTalkthroughProgressData = updateTalkthroughProgress;
export type UpdateTalkthroughProgressVariables =
    updateTalkthroughProgressVariables;
/**
 * Updates the user's progress for Interactive content types.
 */
export const UPDATE_TALKTHROUGH_PROGRESS_MUTATION: GqlOperation<
    UpdateTalkthroughProgressData,
    UpdateTalkthroughProgressVariables
> = gqlOp(gql`
    mutation updateTalkthroughProgress(
        $fullyWatched: Boolean!
        $lastMsWatched: Float!
        $lessonId: ID!
        $msDuration: Int!
        $msWatched: Float!
        $programId: ID!
        $updateProgress: Boolean!
    ) {
        updateTalkthroughProgress(
            input: {
                fullyWatched: $fullyWatched
                lastMsWatched: $lastMsWatched
                lessonId: $lessonId
                msDuration: $msDuration
                msWatched: $msWatched
                programId: $programId
                updateProgress: $updateProgress
            }
        ) {
            actionResults {
                pointsEarned {
                    points
                }
                notificationsAdded {
                    badges
                    avatarParts
                    readable
                    urgent
                    toast
                    continueUrl
                }
                tutorialNodeProgress {
                    contentId
                    progress
                }
                userProfile {
                    countVideosCompleted
                    points
                    countBrandNewNotifications
                }
            }
            programProgress {
                completionStatus
                content {
                    id
                    contentKind
                }
                points
                lastMsWatched
            }
            error {
                code
            }
        }
    }
`);

/**
 * A query to get all of a user's progress for a given course.
 */
export const COURSE_PROGRESS_QUERY: GqlOperation<
    courseProgressQuery,
    courseProgressQueryVariables
> = gqlOp(gql`
    query courseProgressQuery($topicId: String!) {
        user {
            id
            courseProgress(topicId: $topicId) {
                currentMasteryV2 {
                    percentage
                    pointsEarned
                }
                masteryMap {
                    progressKey
                    status
                }
                unitProgresses {
                    currentMasteryV2 {
                        percentage
                        pointsEarned
                    }
                    unitId
                }
            }
            exerciseData {
                masteryChallengeStatus(courseId: $topicId) {
                    totalQuestions
                    isEligible
                    currentAttempt {
                        id
                        canResume
                        timeLeftSeconds
                        expirationTime
                        expirationPeriodHours
                    }
                }
            }
            latestCourseChallengeAttempt(courseId: $topicId) {
                id
                numAttempted
                numCorrect
                completedDate
                canResume
                isCompleted
            }
        }
    }
`);
